import React, { Component } from 'react';
import { photoEditorPropTypes, positionPropTypes } from '../../proptypes';
import Editar from '../../asset/editar.svg';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import loading from '../../asset/image-loader.gif';
import WarnIcon from './warn-icon/';
import KeyboardEventHandler from 'react-keyboard-event-handler';
import { produtosEditaveis } from '../../utils/textosVariaveis';
import { getFundoOpacity } from '../../utils/calculosFundos';
import { isAppEnabled, isFix, isViewMode } from '../../utils/helpers';
import { useSettingsStore } from '../../store/settings';
import {
  startImageLoadWait,
  clearImageLoadWait,
  IMAGE_LOAD_ERROR_MSG,
  IMAGE_LOAD_ERROR_FIX_MSG
} from '../../utils/imageLoadTimeout';
const PLACEHOLDER_SCALE = 0.75;
const ERROR_PLACEHOLDER_SCALE = PLACEHOLDER_SCALE / 2;
const ERROR_PLACEHOLDER_FILL = '#eb0000';

const getPlaceholderMountainBounds = (
  width,
  height,
  scale,
  addLeft = 0,
  addTop = 0
) => ({
  imgWidth: width * scale,
  imgHeight: height * scale,
  imgHalfWidth: (width * (1 - scale)) / 2 + addLeft,
  imgHalfHeight: (height * (1 - scale)) / 2 + addTop
});

const fixPosAux = (
  pos,
  dim,
  shapeDim,
  id,
  rotate,
  fixPos,
  bleed,
  force,
  imgZoom
) => {
  const maxX =
    rotate === 0 || rotate === 180
      ? Math.abs(shapeDim.width - dim.width) * -1
      : Math.abs(shapeDim.height - dim.height) * -1;
  const maxY =
    rotate === 0 || rotate === 180
      ? Math.abs(shapeDim.height - dim.height) * -1
      : Math.abs(shapeDim.width - dim.width) * -1;

  if (pos.x < maxX || pos.y < maxY) {
    fixPos(pos, dim, shapeDim, id, rotate, bleed, imgZoom, force);
  }
};

const ShapeFormat = ({ width, height, type, points }) => {
  switch (type) {
    case 'ellipse':
      return (
        <ellipse
          cx={width / 2}
          cy={height / 2}
          rx={width / 2}
          ry={height / 2}
        />
      );
    case 'triangle':
      return <polygon points={`0,0 ${width},0 0,${height}`}></polygon>;
    case 'custom':
      return <polygon points={points}></polygon>;
    default:
      return <rect width={width} height={height} />;
  }
};

const checkFixPos = (props, prevProps, force = false) => {
  const {
    imgWidth,
    imgHeight,
    bleed,
    rotate,
    imgPos,
    zoom,
    width,
    height,
    id,
    fixPos,
    isPrint
  } = props;
  const isFullHeight = isPrint || (bleed.top > 0 && bleed.bottom > 0);
  if (
    (prevProps && (prevProps.rotate !== rotate || prevProps.zoom !== zoom)) ||
    force
  ) {
    const pos = {
      x: imgPos.x - bleed.left,
      y: imgPos.y - (isFullHeight ? bleed.top : 0)
    };
    const imgZoom = zoom || 1;
    const imgRotate = rotate || 0;
    const imgScale =
      imgRotate === 0 || imgRotate === 180
        ? Math.max(width / imgWidth, height / imgHeight)
        : Math.max(width / imgHeight, height / imgWidth);
    const finalImgWidth = imgWidth * imgScale + bleed.left + bleed.right;
    const finalImgHeight =
      imgHeight * imgScale + (isFullHeight ? bleed.top + bleed.bottom : 0);

    if (prevProps) {
      const prevZoom = prevProps.zoom || 1;
      if (prevZoom !== zoom) {
        pos.x += (finalImgWidth * (prevZoom - (zoom || 1))) / 2;
        pos.y += (finalImgHeight * (prevZoom - (zoom || 1))) / 2;
      }
    }

    if (
      (prevProps && (prevProps.rotate !== rotate || prevProps.zoom !== zoom)) ||
      isPrint
    ) {
      fixPosAux(
        pos,
        {
          width: finalImgWidth * imgZoom,
          height: finalImgHeight * imgZoom
        },
        imgRotate === 0 || imgRotate === 180
          ? { width, height }
          : { width: height, height: width },
        id,
        imgRotate,
        fixPos,
        bleed,
        true,
        imgZoom
      );
    }
  }
};

class Frame extends Component {
  loadTimeoutId = null;

  _prevDisplaySource = null;

  _settingsUnsub = null;

  state = {
    imageLoadTimedOut: false
  };

  getDisplaySource = () => {
    const { source: sourceSrc } = this.props;
    return isAppEnabled() && sourceSrc;
  };

  componentDidMount() {
    checkFixPos(this.props, null, true);
    this._prevDisplaySource = this.getDisplaySource();
    this.syncImageLoadTimeout();
    this._settingsUnsub = useSettingsStore.subscribe(() => {
      if (!isViewMode() && !isFix()) return;

      const displaySource = this.getDisplaySource();
      if (displaySource !== this._prevDisplaySource) {
        this._prevDisplaySource = displaySource;
        this.syncImageLoadTimeout();
        this.forceUpdate();
        requestAnimationFrame(() => ReactTooltip.rebuild());
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    checkFixPos(this.props, prevProps);

    if (prevProps.id !== this.props.id) {
      clearImageLoadWait(prevProps.id);
    }

    const displaySource = this.getDisplaySource();
    if (
      displaySource !== this._prevDisplaySource ||
      prevProps.id !== this.props.id
    ) {
      this._prevDisplaySource = displaySource;
      this.syncImageLoadTimeout();
    }

    if (prevState.imageLoadTimedOut !== this.state.imageLoadTimedOut) {
      requestAnimationFrame(() => ReactTooltip.rebuild());
    }
  }

  componentWillUnmount() {
    this.clearImageLoadTimer();
    if (this._settingsUnsub) {
      this._settingsUnsub();
      this._settingsUnsub = null;
    }
  }

  clearImageLoadTimer = () => {
    if (this.loadTimeoutId != null) {
      clearTimeout(this.loadTimeoutId);
      this.loadTimeoutId = null;
    }
  };

  syncImageLoadTimeout = () => {
    const { id, isPrint } = this.props;
    const displaySource = this.getDisplaySource();
    this.clearImageLoadTimer();

    if (displaySource || isPrint) {
      clearImageLoadWait(id);
      if (this.state.imageLoadTimedOut) {
        this.setState({ imageLoadTimedOut: false });
      }
      return;
    }

    const remaining = startImageLoadWait(id);
    if (remaining <= 0) {
      if (!this.state.imageLoadTimedOut) {
        this.setState({ imageLoadTimedOut: true });
      }
      return;
    }

    if (this.state.imageLoadTimedOut) {
      this.setState({ imageLoadTimedOut: false });
    }

    this.loadTimeoutId = setTimeout(() => {
      this.setState({ imageLoadTimedOut: true });
    }, remaining);
  };

  renderPlaceholderBackground = () => {
    const {
      width,
      height,
      type,
      points,
      background,
      canDrop,
      isOver,
      isBookOverviewExpanded,
      photoEditor
    } = this.props;
    const fill =
      canDrop && !isOver && !isBookOverviewExpanded
        ? '#a7a7a7'
        : background || '#E6E6E6';
    const shapeStyle = {
      fill,
      transition: photoEditor.active ? '' : 'fill 250ms'
    };

    switch (type) {
      case 'ellipse':
        return (
          <ellipse
            cx={width / 2}
            cy={height / 2}
            rx={width / 2}
            ry={height / 2}
            style={shapeStyle}
          />
        );
      case 'triangle':
        return (
          <polygon
            points={`0,0 ${width},0 0,${height}`}
            style={shapeStyle}
          />
        );
      case 'custom':
        return <polygon points={points} style={shapeStyle} />;
      default:
        return (
          <rect x="0" y="0" width={width} height={height} style={shapeStyle} />
        );
    }
  };

  renderLoadError = () => {
    const { width, height, debru, changePos } = this.props;
    const offsetX = changePos.left ? debru : 0;
    const offsetY = changePos.top ? debru : 0;
    const {
      imgWidth,
      imgHeight,
      imgHalfWidth,
      imgHalfHeight
    } = getPlaceholderMountainBounds(
      width,
      height,
      ERROR_PLACEHOLDER_SCALE,
      offsetX,
      offsetY
    );

    return (
      <React.Fragment>
        {this.renderPlaceholderBackground()}
        <svg
          width={imgWidth}
          height={imgHeight}
          viewBox="0 0 48 36"
          x={imgHalfWidth}
          y={imgHalfHeight}
        >
          <circle cx="42" cy="6" r="6" fill={ERROR_PLACEHOLDER_FILL} />
          <path
            d="M12 4 L28 24 L36 18 L48 36 L0 36 L12 4Z"
            fill={ERROR_PLACEHOLDER_FILL}
          />
        </svg>
      </React.Fragment>
    );
  };

  render() {
    const {
      espelhado,
      width,
      height,
      x,
      y,
      id,
      zoom,
      rotate,
      imgWidth,
      imgHeight,
      printSize,
      source: sourceSrc,
      isDragging,
      isDraggingOver,
      mouseStartHandler,
      mouseStopHandler,
      moveHandler,
      moveKeyboardHandler,
      imgPos,
      bleed,
      type,
      connectDropTarget,
      photoEditor,
      updatePhotoEditor,
      isBookOverviewExpanded,
      isPrint,
      debru,
      changePos,
      screenRate,
      updateWarning,
      prodType,
      prodFormat,
      points
    } = this.props;
    const { imageLoadTimedOut } = this.state;
    const source = this.getDisplaySource();
    const showBrokenImagePlaceholder =
      !source && (imageLoadTimedOut || isFix());
    const hasInteractiveImage = Boolean(source) && !showBrokenImagePlaceholder;
    const allowReplaceOnError = imageLoadTimedOut && !isViewMode();
    const isFullHeight = isPrint || (bleed.top > 0 && bleed.bottom > 0);
    const imgZoom = zoom || 1;
    let imgRotate = rotate || 0;

    const imgScale =
      imgRotate === 0 || imgRotate === 180
        ? Math.max(width / imgWidth, height / imgHeight)
        : Math.max(width / imgHeight, height / imgWidth);

    const pos = {
      x: imgPos.x - bleed.left,
      y: imgPos.y - (isFullHeight ? bleed.top : 0)
    };
    const { active, frameId } = photoEditor;

    const finalImgWidth = imgWidth * imgScale + bleed.left + bleed.right;
    const finalImgHeight =
      imgHeight * imgScale + (isFullHeight ? bleed.top + bleed.bottom : 0);
    const loaderHeight = height * 0.075;

    const fixPosOnRotate =
      ((Math.abs(imgWidth - imgHeight) *
        imgScale *
        (imgWidth < imgHeight ? 1 : -1)) /
        2) *
      imgZoom;

    if (document.getElementById(`image-${id}`)) {
      if (active && frameId !== id) {
        document.getElementById(`image-${id}`).style.opacity = 0.25;
      } else {
        document.getElementById(`image-${id}`).style.opacity = '';
      }
    }

    let posicaoX = pos.x;
    let posicaoY = pos.y;
    let calculoRotacaoX =
      pos.x +
      (finalImgWidth * imgZoom) / 2 +
      (imgRotate === 90 ? fixPosOnRotate : 0);
    let calculoRotacaoY =
      pos.y +
      (finalImgHeight * imgZoom) / 2 -
      (imgRotate === 270 ? fixPosOnRotate : 0);

    const imageLoadErrorTip = showBrokenImagePlaceholder
      ? allowReplaceOnError
        ? IMAGE_LOAD_ERROR_FIX_MSG
        : IMAGE_LOAD_ERROR_MSG
      : null;

    return connectDropTarget(
      <svg
        id="connect-drop-target-id"
        width={width}
        height={height}
        x={x}
        y={y}
        style={{
          overflow: 'visible',
          cursor: `${
            !source
              ? showBrokenImagePlaceholder
                ? allowReplaceOnError
                  ? 'copy'
                  : 'default'
                : 'progress'
              : isBookOverviewExpanded
              ? 'default'
              : active && frameId === id
              ? 'move'
              : 'pointer'
          } `
        }}
        opacity={getFundoOpacity()}
        onTouchStart={e => {
          if (!isBookOverviewExpanded && active && frameId === id && source) {
            mouseStartHandler(e, pos, true, id);
          }
        }}
        onMouseDown={e => {
          if (!isBookOverviewExpanded && active && frameId === id && source) {
            mouseStartHandler(e, pos, false, id);
          }
        }}
        onMouseUp={() => {
          if (
            isDragging &&
            !isBookOverviewExpanded &&
            active &&
            frameId === id &&
            source
          ) {
            mouseStopHandler();
          }
        }}
        onMouseLeave={() => {
          if (
            isDragging &&
            !isBookOverviewExpanded &&
            active &&
            frameId === id &&
            source
          ) {
            mouseStopHandler();
          }
        }}
        onTouchEnd={() => {
          if (
            isDragging &&
            !isBookOverviewExpanded &&
            active &&
            frameId === id &&
            source
          ) {
            mouseStopHandler();
          }
        }}
        onTouchMove={e => {
          if (
            isDraggingOver &&
            !isBookOverviewExpanded &&
            active &&
            frameId === id &&
            source
          ) {
            moveHandler(
              e,
              true,
              {
                width: finalImgWidth * imgZoom,
                height: finalImgHeight * imgZoom
              },
              imgRotate === 0 || imgRotate === 180
                ? { width, height }
                : { width: height, height: width },
              id,
              imgRotate,
              bleed,
              imgZoom
            );
          }
        }}
        onMouseMove={e => {
          if (
            isDraggingOver &&
            !isBookOverviewExpanded &&
            active &&
            frameId === id &&
            source
          ) {
            moveHandler(
              e,
              false,
              {
                width: finalImgWidth * imgZoom,
                height: finalImgHeight * imgZoom
              },
              imgRotate === 0 || imgRotate === 180
                ? { width, height }
                : { width: height, height: width },
              id,
              imgRotate,
              bleed,
              imgZoom
            );
          }
        }}
        onMouseOver={() => {
          if (!active && !isBookOverviewExpanded && hasInteractiveImage) {
            document.getElementById(`editar-${id}`).style.opacity = 1;
            document.getElementById(`frame-overlay-${id}`).style.opacity = 0.5;
            const mockupMask = document.getElementById('mockup-mask');
            if (mockupMask != null) mockupMask.style.opacity = 0;
          }
        }}
        onMouseOut={() => {
          if (!active && !isBookOverviewExpanded && hasInteractiveImage) {
            document.getElementById(`editar-${id}`).style.opacity = 0;
            document.getElementById(`frame-overlay-${id}`).style.opacity = 0;
            const mockupMask = document.getElementById('mockup-mask');
            if (mockupMask != null) mockupMask.style.opacity = 1;
          }
        }}
        onClick={() => {
          if (!active && !isBookOverviewExpanded && hasInteractiveImage) {
            updatePhotoEditor(id);
            document.getElementById(`editar-${id}`).style.opacity = 0;
            document.getElementById(`frame-overlay-${id}`).style.opacity = 0;
            const mockupMask = document.getElementById('mockup-mask');
            if (mockupMask != null) mockupMask.style.opacity = 1;
          }
        }}
      >
        <defs>
          <clipPath id={`mask-${id}`}>
            <ShapeFormat
              width={width}
              height={height}
              type={type}
              points={points}
            ></ShapeFormat>
          </clipPath>
        </defs>
        <KeyboardEventHandler
          handleKeys={[
            'left',
            'shift+left',
            'right',
            'shift+right',
            'up',
            'shift+up',
            'down',
            'shift+down',
            'e'
          ]}
          handleFocusableElements={true}
          onKeyEvent={key => {
            if (
              photoEditor.active &&
              active &&
              photoEditor.frameId === id &&
              !isBookOverviewExpanded &&
              source &&
              (key === 'left' ||
                key === 'right' ||
                key === 'down' ||
                key === 'up' ||
                key === 'shift+left' ||
                key === 'shift+right' ||
                key === 'shift+down' ||
                key === 'shift+up')
            ) {
              const keyString = key.includes('shift') ? key.substring(6) : key;
              const step = key.includes('shift') ? 10 : 100;

              moveKeyboardHandler(
                keyString,
                step,
                imgPos,
                {
                  width: finalImgWidth * imgZoom,
                  height: finalImgHeight * imgZoom
                },
                imgRotate === 0 || imgRotate === 180
                  ? { width, height }
                  : { width: height, height: width },
                id,
                imgRotate,
                bleed,
                imgZoom
              );
            } else if (
              key === 'e' &&
              produtosEditaveis() &&
              !photoEditor.active &&
              !active &&
              !isBookOverviewExpanded &&
              hasInteractiveImage
            )
              updatePhotoEditor(id);
          }}
        />
        {isDraggingOver && (
          <image
            xlinkHref={sourceSrc}
            width={finalImgWidth * imgZoom}
            height={finalImgHeight * imgZoom}
            opacity="0.35"
            x={pos.x}
            y={pos.y}
            transform={`rotate(${imgRotate} ${calculoRotacaoX} ${calculoRotacaoY})`}
          />
        )}
        <g clipPath={`url(#mask-${id})`}>
          {source ? (
            <React.Fragment>
              <image
                xlinkHref={sourceSrc}
                width={finalImgWidth * imgZoom}
                height={finalImgHeight * imgZoom}
                x={posicaoX}
                y={posicaoY}
                id={`image-${id}`}
                transform={`rotate(${imgRotate} ${calculoRotacaoX} ${calculoRotacaoY})`}
                opacity={isDragging && !isDraggingOver ? 0.125 : 1}
              />
              <rect
                id={`frame-overlay-${id}`}
                x="0"
                y="0"
                width={width}
                height={height}
                fill="#FFFFFF"
                opacity={0}
              />
            </React.Fragment>
          ) : (
            !isPrint &&
            (showBrokenImagePlaceholder ? (
              this.renderLoadError()
            ) : (
              <image
                xlinkHref={loading}
                height={loaderHeight}
                x={width / 2 - loaderHeight * 2 + (changePos.left ? debru : 0)}
                y={height / 2 - height * 0.1 + (changePos.top ? debru : 0)}
                id={`loader-${id}`}
              />
            ))
          )}
        </g>
        {hasInteractiveImage && (
          <foreignObject
            width={width * 0.2}
            height={height * 0.2}
            x={width / 2 - width * 0.1 + (changePos.left ? debru : 0)}
            y={height / 2 - height * 0.1 + (changePos.top ? debru : 0)}
          >
            <img
              src={Editar}
              alt="Ícone editar"
              id={`editar-${id}`}
              draggable="false"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                opacity: 0
              }}
            />
          </foreignObject>
        )}
        {!isPrint && !espelhado && !imageLoadTimedOut && !showBrokenImagePlaceholder && (
          <WarnIcon
            id={id}
            width={width}
            height={height}
            screenRate={screenRate}
            printSize={printSize}
            changePos={changePos}
            bleed={bleed}
            type={type}
            debru={debru}
            photoEditorActive={photoEditor.active}
            updateWarning={updateWarning}
            prodType={prodType}
            prodFormat={prodFormat}
          />
        )}
        {imageLoadErrorTip && (
          <foreignObject
            x={0}
            y={0}
            width={width}
            height={height}
            className="frame-load-error-hitarea"
            pointerEvents={isDraggingOver ? 'none' : 'all'}
          >
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              className="frame-load-error-hitarea"
              style={{
                width: `${width}px`,
                height: `${height}px`,
                pointerEvents: isDraggingOver ? 'none' : 'all'
              }}
              data-tip={imageLoadErrorTip}
              data-place="bottom"
              data-class="type-warning"
              data-effect="float"
            />
          </foreignObject>
        )}
      </svg>
    );
  }
}

Frame.propTypes = {
  espelhado: PropTypes.bool,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  zoom: PropTypes.number,
  rotate: PropTypes.number,
  imgWidth: PropTypes.number.isRequired,
  imgHeight: PropTypes.number.isRequired,
  printSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired,
  source: PropTypes.string.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isDraggingOver: PropTypes.bool.isRequired,
  mouseStartHandler: PropTypes.func.isRequired,
  mouseStopHandler: PropTypes.func.isRequired,
  moveHandler: PropTypes.func.isRequired,
  moveKeyboardHandler: PropTypes.func.isRequired,
  fixPos: PropTypes.func.isRequired,
  imgPos: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }).isRequired,
  bleed: positionPropTypes,
  type: PropTypes.string.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  photoEditor: photoEditorPropTypes,
  updatePhotoEditor: PropTypes.func.isRequired,
  isBookOverviewExpanded: PropTypes.bool.isRequired,
  isPrint: PropTypes.bool.isRequired,
  screenRate: PropTypes.number.isRequired,
  debru: PropTypes.number.isRequired,
  changePos: PropTypes.shape({
    top: PropTypes.bool.isRequired,
    left: PropTypes.bool.isRequired
  }).isRequired,
  prodFormat: PropTypes.string.isRequired,
  prodType: PropTypes.string.isRequired,
  fundoColor: PropTypes.string.isRequired,
  background: PropTypes.string.isRequired,
  points: PropTypes.string,
  canDrop: PropTypes.bool,
  isOver: PropTypes.bool
};

Frame.defaultProps = {
  canDrop: false,
  isOver: false
};

export default Frame;
